import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Pencil, Settings, Eye, Loader2 } from 'lucide-react';
import { useAuthStore } from '@/stores/auth-store';
import { useUIStore } from '@/stores/ui-store';
import { useDesignPreviewStore } from '@/stores/design-preview-store';
import { canAccessAdminShell } from '@/lib/roles';

export default function AdminBar() {
  const user = useAuthStore((s) => s.user);
  const startPreview = useAuthStore((s) => s.startPreview);
  const quickEditEnabled = useUIStore((s) => s.quickEditEnabled);
  const toggleQuickEdit = useUIStore((s) => s.toggleQuickEdit);
  const isPreviewFrame = useDesignPreviewStore((s) => s.isPreviewFrame);
  const navigate = useNavigate();
  const [startingPreview, setStartingPreview] = useState(false);

  // Never render inside the Design Sandbox preview iframe — it would overlay the
  // design being previewed.
  if (isPreviewFrame) return null;

  // Hidden once preview is engaged (role flips to 'user'); the PreviewBanner
  // takes over with the Exit control. Designers get the bar too — inline
  // editing is the point of the role.
  if (!canAccessAdminShell(user?.role)) return null;

  async function handleViewAsCustomer() {
    setStartingPreview(true);
    try {
      await startPreview();
      navigate('/');
    } catch {
      setStartingPreview(false);
    }
  }

  return (
    <div className="fixed bottom-4 left-4 z-[60] flex items-center gap-3 rounded-full bg-gray-900/90 px-4 py-2 text-white shadow-xl backdrop-blur-sm">
      <button
        onClick={toggleQuickEdit}
        className="flex items-center gap-2 text-sm font-medium transition-colors hover:text-blue-300"
        role="switch"
        aria-checked={quickEditEnabled}
        aria-label="Toggle quick edit mode"
      >
        <Pencil className="h-3.5 w-3.5" />
        <span className="hidden sm:inline">Quick Edit</span>
        <span
          className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${
            quickEditEnabled ? 'bg-blue-500' : 'bg-gray-600'
          }`}
        >
          <span
            className={`inline-block h-3.5 w-3.5 rounded-full bg-white transition-transform ${
              quickEditEnabled ? 'translate-x-4.5' : 'translate-x-1'
            }`}
          />
        </span>
      </button>
      <span className="h-4 w-px bg-gray-600" aria-hidden="true" />
      <button
        onClick={handleViewAsCustomer}
        disabled={startingPreview}
        className="flex items-center gap-2 text-sm font-medium transition-colors hover:text-blue-300 disabled:opacity-60"
        title="Experience the site as a regular customer (admin tools off, auto-reverts)"
      >
        {startingPreview ? (
          <Loader2 className="h-3.5 w-3.5 animate-spin" />
        ) : (
          <Eye className="h-3.5 w-3.5" />
        )}
        <span className="hidden sm:inline">View as customer</span>
      </button>
      <span className="h-4 w-px bg-gray-600" aria-hidden="true" />
      <Link
        to="/admin"
        className="flex items-center gap-1.5 text-sm font-medium transition-colors hover:text-blue-300"
      >
        <Settings className="h-3.5 w-3.5" />
        <span className="hidden sm:inline">Admin</span>
      </Link>
    </div>
  );
}
